import { createContext, useContext, useState, useCallback, useRef, useEffect, lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { ceremonyApi, type ShareCardData } from "@/api/ceremony.api";

export type CeremonyTier = "quiet" | "celebration" | "major" | "defining";

export interface CeremonyEvent {
  id: string;
  tier: CeremonyTier;
  title: string;
  message: string;
  extras?: {
    xp?: number;
    sp?: number;
    levelName?: string;
    frameColor?: string;
    showName?: string;
    ceremonyKey?: string;
    showId?: number;
    goldStarNumber?: number;
    totalStarlets?: number;
    badgeName?: string;
    badgeSlug?: string;
  };
}

interface CeremonyState {
  showCeremony: (tier: CeremonyTier, title: string, message: string, extras?: CeremonyEvent["extras"]) => void;
}

const CeremonyContext = createContext<CeremonyState | null>(null);

const TIER_DURATIONS: Record<CeremonyTier, number> = {
  quiet: 3000,
  celebration: 5000,
  major: 0,      // requires manual dismiss
  defining: 0,   // requires manual dismiss
};

// Lazy load ShareCard to avoid blocking initial render
const ShareCardLazy = lazy(() =>
  import("@/components/gamification/ShareCard").then((m) => ({ default: m.ShareCard }))
);

export function CeremonyProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<CeremonyEvent[]>([]);
  const [active, setActive] = useState<CeremonyEvent | null>(null);
  const [shareCardData, setShareCardData] = useState<ShareCardData | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setActive(null);
  }, []);

  const closeShareCard = useCallback(() => {
    setShareCardData(null);
  }, []);

  const openShareCard = useCallback(async (ceremonyKey: string) => {
    try {
      const data = await ceremonyApi.getShareCard(ceremonyKey);
      setShareCardData(data);
    } catch {
      // If share card fails to load, silently ignore
    }
  }, []);

  // Process queue: show next event when active is cleared
  useEffect(() => {
    if (active || queue.length === 0) return;

    const next = queue[0];
    setQueue((q) => q.slice(1));
    setActive(next);

    const duration = TIER_DURATIONS[next.tier];
    if (duration > 0) {
      timerRef.current = setTimeout(dismiss, duration);
    }
  }, [active, queue, dismiss]);

  const showCeremony = useCallback(
    (tier: CeremonyTier, title: string, message: string, extras?: CeremonyEvent["extras"]) => {
      const event: CeremonyEvent = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        tier,
        title,
        message,
        extras,
      };
      setQueue((q) => [...q, event]);
    },
    []
  );

  return (
    <CeremonyContext.Provider value={{ showCeremony }}>
      {children}
      {active && (
        <CeremonyOverlay
          event={active}
          onDismiss={dismiss}
          onShare={openShareCard}
        />
      )}
      {shareCardData && (
        <Suspense fallback={null}>
          <ShareCardLazy cardData={shareCardData} onClose={closeShareCard} />
        </Suspense>
      )}
      <CeremonyStyles />
    </CeremonyContext.Provider>
  );
}

export function useCeremony(): CeremonyState {
  const ctx = useContext(CeremonyContext);
  if (!ctx) throw new Error("useCeremony must be used within CeremonyProvider");
  return ctx;
}

/* ------------------------------------------------------------------ */
/* CSS Keyframe Animations (injected once)                            */
/* ------------------------------------------------------------------ */

function CeremonyStyles() {
  return (
    <style>{`
      @keyframes ceremony-slide-in {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes ceremony-slide-down {
        from { transform: translateY(-100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes ceremony-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes ceremony-scale-in {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      @keyframes ceremony-glow-pulse {
        0%, 100% { box-shadow: 0 0 20px rgba(234, 172, 85, 0.1); }
        50% { box-shadow: 0 0 40px rgba(234, 172, 85, 0.3); }
      }
      @keyframes ceremony-particle-float {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(-80px) rotate(180deg); opacity: 0; }
      }
      @keyframes ceremony-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes ceremony-star-spin {
        0% { transform: scale(0) rotate(-180deg); opacity: 0; }
        60% { transform: scale(1.2) rotate(0deg); opacity: 1; }
        100% { transform: scale(1) rotate(0deg); opacity: 1; }
      }
      @keyframes ceremony-text-reveal {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      @keyframes ceremony-accent-grow {
        from { width: 0; }
        to { width: 100%; }
      }
      @keyframes ceremony-defining-bg {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      .ceremony-slide-in { animation: ceremony-slide-in 0.4s ease-out forwards; }
      .ceremony-slide-down { animation: ceremony-slide-down 0.5s ease-out forwards; }
      .ceremony-fade-in { animation: ceremony-fade-in 0.6s ease-out forwards; }
      .ceremony-scale-in { animation: ceremony-scale-in 0.6s ease-out forwards; }
      .ceremony-glow-pulse { animation: ceremony-glow-pulse 2s ease-in-out infinite; }
      .ceremony-text-reveal { animation: ceremony-text-reveal 0.6s ease-out forwards; }
      .ceremony-star-spin { animation: ceremony-star-spin 1s ease-out forwards; }
    `}</style>
  );
}

/* ------------------------------------------------------------------ */
/* Render layer                                                       */
/* ------------------------------------------------------------------ */

interface OverlayProps {
  event: CeremonyEvent;
  onDismiss: () => void;
  onShare: (ceremonyKey: string) => void;
}

function CeremonyOverlay({ event, onDismiss, onShare }: OverlayProps) {
  switch (event.tier) {
    case "quiet":
      return <QuietToast event={event} />;
    case "celebration":
      return <CelebrationToast event={event} onShare={onShare} />;
    case "major":
      return <MajorBanner event={event} onDismiss={onDismiss} onShare={onShare} />;
    case "defining":
      return <DefiningOverlay event={event} onDismiss={onDismiss} onShare={onShare} />;
  }
}

/* ─── Quiet: sub-1s feel toast, slide-in, auto-dismiss 3s ─── */
function QuietToast({ event }: { event: CeremonyEvent }) {
  return (
    <div className="fixed top-4 right-4 z-[60] max-w-xs ceremony-slide-in">
      <div
        className="rounded-lg border border-glass-border/40 shadow-lg px-4 py-3"
        style={{ background: "linear-gradient(135deg, #313949, #1e1f2c)" }}
      >
        <p className="text-xs font-bold text-text-primary">{event.title}</p>
        <p className="text-[10px] text-text-secondary mt-0.5">{event.message}</p>
        {!!event.extras?.xp && (
          <span className="text-[10px] font-bold text-accent-light mt-1 inline-block">
            +{event.extras.xp} XP
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Celebration: 2-5s with accent bar + particles ─── */
function CelebrationToast({ event }: { event: CeremonyEvent; onShare: (key: string) => void }) {
  const accentColor = event.extras?.frameColor || "#b87800";

  return (
    <div className="fixed top-4 right-4 z-[60] max-w-sm ceremony-slide-in">
      <div
        className="rounded-lg border border-glass-border/40 shadow-xl overflow-hidden ceremony-glow-pulse"
        style={{ background: "linear-gradient(135deg, #313949, #1e1f2c)" }}
      >
        {/* Animated accent bar */}
        <div className="relative h-1 overflow-hidden">
          <div
            style={{
              height: "100%",
              background: `linear-gradient(90deg, ${accentColor}, #eaac55, ${accentColor})`,
              backgroundSize: "200% 100%",
              animation: "ceremony-shimmer 2s linear infinite",
            }}
          />
        </div>
        {/* Particle effects (CSS pseudo-element simulation) */}
        <div className="relative">
          <CelebrationParticles color={accentColor} />
          <div className="px-4 py-3 relative">
            <p className="text-sm font-bold text-text-primary ceremony-text-reveal">{event.title}</p>
            <p className="text-xs text-text-secondary mt-1 ceremony-text-reveal" style={{ animationDelay: "0.1s" }}>
              {event.message}
            </p>
            <div className="flex items-center gap-3 mt-2">
              {!!event.extras?.xp && (
                <span className="text-[10px] font-bold text-accent-light ceremony-text-reveal" style={{ animationDelay: "0.2s" }}>
                  +{event.extras.xp} XP
                </span>
              )}
              {!!event.extras?.sp && (
                <span className="text-[10px] font-bold text-warning ceremony-text-reveal" style={{ animationDelay: "0.25s" }}>
                  +{event.extras.sp} SP
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Particle effect: small floating dots */
function CelebrationParticles({ color }: { color: string }) {
  const particles = Array.from({ length: 6 }, (_, i) => ({
    left: `${15 + i * 14}%`,
    delay: `${i * 0.3}s`,
    size: 3 + (i % 3),
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ height: "60px" }}>
      {particles.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: p.left,
            bottom: "0",
            width: `${p.size}px`,
            height: `${p.size}px`,
            borderRadius: "50%",
            background: i % 2 === 0 ? color : "#eaac55",
            opacity: 0,
            animation: `ceremony-particle-float 1.5s ease-out ${p.delay} forwards`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Major: full-width slide-down banner, dark backdrop, share option ─── */
function MajorBanner({ event, onDismiss, onShare }: { event: CeremonyEvent; onDismiss: () => void; onShare: (key: string) => void }) {
  const accentColor = event.extras?.frameColor || "#b87800";
  const hasCeremonyKey = !!event.extras?.ceremonyKey;

  return (
    <>
      {/* Semi-transparent backdrop */}
      <div
        className="fixed inset-0 z-[59] ceremony-fade-in"
        style={{ background: "rgba(0,0,0,0.4)" }}
        onClick={onDismiss}
      />
      {/* Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] ceremony-slide-down">
        <div
          className="border-b shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${accentColor}15, #1e1f2c 40%, ${accentColor}08)`,
            borderColor: `${accentColor}30`,
          }}
        >
          {/* Shimmer accent line */}
          <div className="h-[2px] overflow-hidden">
            <div
              style={{
                height: "100%",
                background: `linear-gradient(90deg, transparent, ${accentColor}, #eaac55, ${accentColor}, transparent)`,
                backgroundSize: "200% 100%",
                animation: "ceremony-shimmer 3s linear infinite",
              }}
            />
          </div>
          <div className="max-w-3xl mx-auto px-6 py-5">
            {/* Gold star display for Gold Star ceremonies */}
            {!!event.extras?.goldStarNumber && (
              <div className="ceremony-star-spin mb-2" style={{ color: "#eaac55", fontSize: "28px", textAlign: "center", letterSpacing: "4px" }}>
                {"★".repeat(Math.min(event.extras.goldStarNumber, 10))}
              </div>
            )}
            <div className="ceremony-text-reveal">
              <p className="text-lg font-extrabold text-text-primary tracking-wide">{event.title}</p>
            </div>
            <div className="ceremony-text-reveal" style={{ animationDelay: "0.15s" }}>
              <p className="text-sm text-text-secondary mt-1 leading-relaxed max-w-xl">{event.message}</p>
            </div>
            <div className="flex items-center gap-3 mt-2 ceremony-text-reveal" style={{ animationDelay: "0.3s" }}>
              {!!event.extras?.xp && (
                <span className="text-xs font-bold text-accent-light">+{event.extras.xp} XP</span>
              )}
              {!!event.extras?.sp && (
                <span className="text-xs font-bold text-warning">+{event.extras.sp} SP</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4 ceremony-text-reveal" style={{ animationDelay: "0.45s" }}>
              {hasCeremonyKey && (
                <button
                  onClick={() => {
                    onShare(event.extras!.ceremonyKey!);
                    onDismiss();
                  }}
                  className="px-4 py-2 rounded text-[10px] font-bold uppercase tracking-widest transition-colors"
                  style={{ background: `${accentColor}20`, color: accentColor, border: `1px solid ${accentColor}40` }}
                >
                  Share
                </button>
              )}
              <button
                onClick={onDismiss}
                className="px-4 py-2 rounded text-[10px] font-bold uppercase tracking-widest text-text-muted hover:text-text-primary transition-colors"
                style={{ background: "#313949" }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Defining: full-screen ceremony, animated sequence, 30s feel ─── */
function DefiningOverlay({ event, onDismiss, onShare }: { event: CeremonyEvent; onDismiss: () => void; onShare: (key: string) => void }) {
  const isShowPerfection = event.extras?.ceremonyKey === "show_perfection";

  if (isShowPerfection) {
    return <ShowPerfectionOverlay event={event} onDismiss={onDismiss} onShare={onShare} />;
  }

  return <GenericDefiningOverlay event={event} onDismiss={onDismiss} onShare={onShare} />;
}

/* ─── Show Perfection: bespoke ceremony with show name, star arc, stats ─── */
function ShowPerfectionOverlay({ event, onDismiss, onShare }: { event: CeremonyEvent; onDismiss: () => void; onShare: (key: string) => void }) {
  const accentColor = "#eaac55";
  const hasCeremonyKey = !!event.extras?.ceremonyKey;
  const [phase, setPhase] = useState(0);

  // Phase 0 (0-1s): Dark backdrop with star particles
  // Phase 1 (1-2.5s): Show name + "SHOW PERFECTION" subtitle
  // Phase 2 (2.5-4s): 10 gold star arc
  // Phase 3 (4s+): Stats and share buttons
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1000);
    const t2 = setTimeout(() => setPhase(2), 2500);
    const t3 = setTimeout(() => setPhase(3), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center ceremony-fade-in">
      {/* Deep dark backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, #0a0818, #12102a, #0a0818, #12102a)",
          backgroundSize: "400% 400%",
          animation: "ceremony-defining-bg 8s ease infinite",
        }}
      />
      {/* Warm gold radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(circle at 50% 40%, rgba(234,172,85,0.12) 0%, transparent 55%)",
        }}
      />
      {/* Subtle floating star particles (phase 0+) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 12 }, (_, i) => (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${8 + (i * 7.5) % 90}%`,
              top: `${15 + (i * 13) % 70}%`,
              color: "#eaac55",
              fontSize: `${6 + (i % 4) * 2}px`,
              opacity: 0,
              animation: `ceremony-particle-float ${2 + (i % 3)}s ease-out ${i * 0.4}s infinite`,
            }}
          >
            ★
          </span>
        ))}
      </div>

      <div
        className="relative text-center max-w-lg mx-auto px-8 py-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Phase 1: Show name with golden glow + "SHOW PERFECTION" subtitle */}
        {phase >= 1 && (
          <div className="ceremony-scale-in mb-6">
            <p
              className="text-3xl font-extrabold text-text-primary uppercase tracking-wider"
              style={{
                textShadow: "0 0 40px rgba(234,172,85,0.5), 0 0 80px rgba(234,172,85,0.2)",
              }}
            >
              {event.extras?.showName || event.title}
            </p>
            <p
              className="text-sm font-bold uppercase tracking-[0.3em] mt-3"
              style={{
                color: "#eaac55",
                textShadow: "0 0 20px rgba(234,172,85,0.4)",
              }}
            >
              SHOW PERFECTION
            </p>
          </div>
        )}

        {/* Phase 2: 10 gold star icons in an arc */}
        {phase >= 2 && (
          <div className="relative mx-auto mb-8" style={{ width: "280px", height: "100px" }}>
            {Array.from({ length: 10 }, (_, i) => {
              const angle = Math.PI + (Math.PI * i) / 9;
              const cx = 140 + Math.cos(angle) * 120;
              const cy = 95 + Math.sin(angle) * 80;
              return (
                <span
                  key={i}
                  className="ceremony-star-spin absolute"
                  style={{
                    left: `${cx}px`,
                    top: `${cy}px`,
                    transform: "translate(-50%, -50%)",
                    color: "#eaac55",
                    fontSize: "22px",
                    animationDelay: `${i * 0.08}s`,
                    filter: "drop-shadow(0 0 6px rgba(234,172,85,0.6))",
                  }}
                >
                  ★
                </span>
              );
            })}
          </div>
        )}

        {/* Phase 2 continued: message */}
        {phase >= 2 && (
          <div className="ceremony-text-reveal">
            <p className="text-base text-text-secondary leading-relaxed max-w-md mx-auto">
              {event.message}
            </p>
          </div>
        )}

        {/* Phase 3: Stats + share/dismiss buttons */}
        {phase >= 3 && (
          <div className="ceremony-text-reveal mt-6">
            <div className="flex items-center justify-center gap-6 mb-8">
              {!!event.extras?.xp && (
                <span className="text-sm font-bold text-accent-light">+{event.extras.xp} XP</span>
              )}
              {!!event.extras?.sp && (
                <span className="text-sm font-bold text-warning">+{event.extras.sp} SP</span>
              )}
              {!!event.extras?.totalStarlets && (
                <span className="text-sm font-bold" style={{ color: accentColor }}>
                  {event.extras.totalStarlets.toLocaleString()} Starlets
                </span>
              )}
            </div>
            <div className="flex items-center justify-center gap-3">
              {hasCeremonyKey && (
                <button
                  onClick={() => {
                    onShare(event.extras!.ceremonyKey!);
                    onDismiss();
                  }}
                  className="px-6 py-3 rounded text-xs font-bold uppercase tracking-widest transition-all"
                  style={{
                    background: "linear-gradient(135deg, #eaac55, #f0c96e)",
                    color: "#1a1b27",
                    boxShadow: "0 0 20px rgba(234,172,85,0.4)",
                  }}
                >
                  Share This Moment
                </button>
              )}
              <button
                onClick={onDismiss}
                className="px-6 py-3 rounded text-xs font-bold uppercase tracking-widest text-text-muted hover:text-text-primary transition-colors"
                style={{ background: "#31394980", border: "1px solid #3a3a5c" }}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Generic Defining: original phase-based animation for non-perfection ceremonies ─── */
function GenericDefiningOverlay({ event, onDismiss, onShare }: { event: CeremonyEvent; onDismiss: () => void; onShare: (key: string) => void }) {
  const accentColor = event.extras?.frameColor || "#eaac55";
  const hasCeremonyKey = !!event.extras?.ceremonyKey;
  const [phase, setPhase] = useState(0);

  // Staged reveal: phase 0 -> 1 -> 2 -> 3 over several seconds
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);   // Stars appear
    const t2 = setTimeout(() => setPhase(2), 2200);  // Title + message
    const t3 = setTimeout(() => setPhase(3), 4000);  // Stats + buttons
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center ceremony-fade-in">
      {/* Animated background */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, #0d0b1a, #1a1b27, #0d0b1a, #1a1b27)`,
          backgroundSize: "400% 400%",
          animation: "ceremony-defining-bg 8s ease infinite",
        }}
      />
      {/* Radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 40%, ${accentColor}15 0%, transparent 60%)`,
        }}
      />

      <div
        className="relative text-center max-w-lg mx-auto px-8 py-16"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gold Stars — phase 1 */}
        {phase >= 1 && (
          <div className="ceremony-star-spin mb-6" style={{ color: "#eaac55", fontSize: "40px", letterSpacing: "6px" }}>
            {event.extras?.goldStarNumber
              ? "★".repeat(Math.min(event.extras.goldStarNumber, 10))
              : "★"}
          </div>
        )}

        {/* Title — phase 2 */}
        {phase >= 2 && (
          <div className="ceremony-scale-in">
            <p
              className="text-3xl font-extrabold text-text-primary uppercase tracking-wider"
              style={{
                textShadow: `0 0 40px ${accentColor}50, 0 0 80px ${accentColor}20`,
              }}
            >
              {event.title}
            </p>
            <p className="text-base text-text-secondary mt-4 leading-relaxed max-w-md mx-auto">
              {event.message}
            </p>
          </div>
        )}

        {/* Stats + buttons — phase 3 */}
        {phase >= 3 && (
          <div className="ceremony-text-reveal mt-6">
            <div className="flex items-center justify-center gap-6 mb-8">
              {!!event.extras?.xp && (
                <span className="text-sm font-bold text-accent-light">+{event.extras.xp} XP</span>
              )}
              {!!event.extras?.sp && (
                <span className="text-sm font-bold text-warning">+{event.extras.sp} SP</span>
              )}
              {!!event.extras?.totalStarlets && (
                <span className="text-sm font-bold" style={{ color: accentColor }}>
                  {event.extras.totalStarlets.toLocaleString()} Starlets
                </span>
              )}
            </div>
            <div className="flex items-center justify-center gap-3">
              {hasCeremonyKey && (
                <button
                  onClick={() => {
                    onShare(event.extras!.ceremonyKey!);
                    onDismiss();
                  }}
                  className="px-6 py-3 rounded text-xs font-bold uppercase tracking-widest transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${accentColor}, #eaac55)`,
                    color: "#1a1b27",
                    boxShadow: `0 0 20px ${accentColor}40`,
                  }}
                >
                  Share This Moment
                </button>
              )}
              <button
                onClick={onDismiss}
                className="px-6 py-3 rounded text-xs font-bold uppercase tracking-widest text-text-muted hover:text-text-primary transition-colors"
                style={{ background: "#31394980", border: "1px solid #3a3a5c" }}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
